import React, { ReactNode } from 'react';
import { Box, Paper, Stack, Typography, Button, Chip, Divider } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  value: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  {
    label: 'Clases',
    value: '/home',
    icon: <EventIcon fontSize="small" />,
  },
  {
    label: 'Alumnos',
    value: '/students',
    icon: <SportsKabaddiIcon fontSize="small" />,
  },
  {
    label: 'Perfil',
    value: '/profile',
    icon: <PersonIcon fontSize="small" />,
  },
];

const getPageLabel = (pathname: string) => {
  if (pathname.startsWith('/students/')) {
    return 'Ficha de alumno';
  }

  const currentItem =
    navItems.find((item) => pathname === item.value) ??
    navItems.find((item) => pathname.startsWith(item.value));

  return currentItem?.label ?? 'Panel';
};

const getSelectedValue = (pathname: string) => {
  if (pathname === '/') {
    return '/home';
  }

  if (pathname.startsWith('/students')) {
    return '/students';
  }

  return pathname;
};

export default function InstructorDesktopLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = getPageLabel(location.pathname);
  const selectedValue = getSelectedValue(location.pathname);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f3f3f3',
        px: { xs: 2, lg: 3 },
        py: { xs: 2, lg: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: '280px minmax(0, 1fr)',
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 24,
            borderRadius: 6,
            overflow: 'hidden',
            border: '1px solid rgba(18,18,18,0.08)',
            background: 'linear-gradient(180deg, rgba(14,14,14,0.98) 0%, rgba(29,29,29,0.98) 100%)',
            color: '#f8f2e7',
          }}
        >
          <Box sx={{ p: 3 }}>
            <Chip
              label="Instructores"
              size="small"
              sx={{
                mb: 2,
                bgcolor: 'rgba(255,255,255,0.08)',
                color: '#f8f2e7',
                borderRadius: 1.5,
              }}
            />
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              La Cúpula
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

          <Stack spacing={1} sx={{ p: 2 }}>
            {navItems.map((item) => {
              const selected = selectedValue === item.value;

              return (
                <Button
                  key={item.value}
                  fullWidth
                  startIcon={item.icon}
                  onClick={() => navigate(item.value)}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    color: selected ? '#111111' : '#f8f2e7',
                    bgcolor: selected ? '#ffffff' : 'transparent',
                    '&:hover': {
                      bgcolor: selected ? '#ffffff' : 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>
        </Paper>

        <Box sx={{ minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, lg: 3.5 },
              borderRadius: 6,
              mb: 3,
              border: '1px solid rgba(18,18,18,0.08)',
              backgroundColor: 'white',
            }}
          >
            <Typography
              variant="overline"
              sx={{ letterSpacing: '0.14em', color: 'text.secondary', display: 'block', mb: 1 }}
            >
              Panel de instructor
            </Typography>
            <Typography variant="h3">{currentPage}</Typography>
          </Paper>

          <Box>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
